// Sound controls for the HUD: mute, master volume, source toggle, and music/effects sliders behind
// a disclosure. Styled with the HUD tokens in src/ui/theme.css. The integrator mounts it, e.g. in
// the top bar: <MusicControl music={music} />.
//
//   layout 'inline'   [🔈] ───●── [Orchestra] [v]      the disclosure holds music and effects
//   layout 'compact'  [🔈] [v]                         the disclosure holds every slider and the source

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import './music.css';
import { fromPercent, muteLabel, otherSource, percent, showsMuted, sliderLabel, sourceLabel } from './controls.ts';
import type { Channel, Music, MusicSnapshot } from './types.ts';

const SLIDER_CHANNELS: readonly Channel[] = ['music', 'effects'];

const SPEAKER_STROKE = 1.7;

function useSnapshot(music: Music): MusicSnapshot {
  const [snapshot, setSnapshot] = useState(music.getSnapshot());
  useEffect(() => {
    setSnapshot(music.getSnapshot());
    return music.subscribe(() => setSnapshot(music.getSnapshot()));
  }, [music]);
  return snapshot;
}

function Speaker({ muted }: { muted: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width={SPEAKER_STROKE} stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z" fill="currentColor" fill-opacity="0.28" />
      {muted ? (
        <path d="M16 9.5l5 5M21 9.5l-5 5" />
      ) : (
        <>
          <path d="M15.4 9.2a4 4 0 0 1 0 5.6" />
          <path d="M18 6.8a7.4 7.4 0 0 1 0 10.4" />
        </>
      )}
    </svg>
  );
}

interface SliderProps {
  music: Music;
  channel: Channel;
  value: number;
}

function Slider({ music, channel, value }: SliderProps): JSX.Element {
  return (
    <input
      class="wc-music-slider"
      type="range"
      min="0"
      max="100"
      step="1"
      value={percent(value)}
      aria-label={sliderLabel(channel)}
      onInput={(event) => music.setVolume(channel, fromPercent(Number(event.currentTarget.value)))}
    />
  );
}

/** 'inline' shows the master slider and the source beside the mute button; 'compact' (a crowded bar) folds them into the disclosure. */
export type MusicLayout = 'inline' | 'compact';

function SourceToggle({ music, settings }: { music: Music; settings: MusicSnapshot['settings'] }): JSX.Element {
  const next = otherSource(settings.source);
  return (
    <button
      type="button"
      class="wc-music-source"
      aria-label={`Music source: ${sourceLabel(settings.source)}. Switch to ${sourceLabel(next)}`}
      title={`Switch to ${sourceLabel(next)}`}
      onClick={() => music.setSource(next)}
    >
      {sourceLabel(settings.source)}
    </button>
  );
}

export function MusicControl({ music, layout = 'inline' }: { music: Music; layout?: MusicLayout }): JSX.Element | null {
  const { settings, status } = useSnapshot(music);
  if (status === 'unavailable') {
    return null;
  }

  const inline = layout === 'inline';
  const panelChannels: readonly Channel[] = inline ? SLIDER_CHANNELS : ['master', ...SLIDER_CHANNELS];
  return (
    <div class={inline ? 'wc-music' : 'wc-music wc-music--compact'} data-status={status}>
      <button
        type="button"
        class="wc-ibtn wc-music-mute"
        aria-pressed={settings.muted}
        aria-label={muteLabel(settings)}
        title={muteLabel(settings)}
        onClick={() => music.mute('toggle')}
      >
        <Speaker muted={showsMuted(settings)} />
      </button>

      {inline ? <Slider music={music} channel="master" value={settings.master} /> : null}
      {inline ? <SourceToggle music={music} settings={settings} /> : null}

      <details class="wc-music-more">
        <summary aria-label="More sound settings" title="More sound settings" />
        <div class="wc-music-panel">
          {panelChannels.map((channel) => (
            <label key={channel} class="wc-music-row">
              <span>{sliderLabel(channel).replace(' volume', '')}</span>
              <Slider music={music} channel={channel} value={settings[channel]} />
            </label>
          ))}
          {inline ? null : <SourceToggle music={music} settings={settings} />}
        </div>
      </details>
    </div>
  );
}
